/**
 * Organisation-driven date/time DISPLAY formatting.
 *
 * Plugin (and host) surfaces render dates in the organisation's configured
 * format. This module holds the module-level format refs, the org-aware display
 * formatters, and the MUI X (dayjs) input-mask derivations. It is pure — no React,
 * no dayjs — so it lives in core-utils behind the
 * `@ethisyscore/core-utils/date/org-format` sub-path rather than the main barrel,
 * whose `formatDate` is the ISO `(date: Date) => string` helper, a different
 * contract from the org-aware `formatDate` exported here.
 *
 * The formatters do NOT re-implement parse/format/fallback: they resolve the
 * effective format (the org ref when grammar-valid, else the UK fallback) and
 * delegate to the shared `formatDateSafe` primitive. The refs are stamped by the
 * surface sync hook — the SDK `useOrgFormatSync` reads the
 * `ethisys:organisation-settings` reserved resource and calls `setOrgFormats`.
 * When no org format is set (or a stored value fails the shared token grammar)
 * formatters fall back to the deterministic UK default, NOT the OS locale, so
 * output is machine-stable regardless of the host.
 *
 * Consolidated from the per-plugin `adapter/shared/{orgFormatContextValue,dates}`
 * copies and the monolith's `dateUtils` mask helpers, which re-export from here
 * so every existing import path keeps working.
 */
import { MS_PER_MINUTE } from "./constants";
import { formatDateSafe, relativeTimePhrase } from "./format";
import { validateDateFormat, validateTimeFormat } from "./formatValidation";

/** UK display fallback (date-fns tokens) — numeric `dd/MM/yyyy`. */
export const ORG_DATE_FALLBACK_FORMAT = "dd/MM/yyyy";
/** UK time display fallback (date-fns tokens) — 24-hour `HH:mm`. */
export const ORG_TIME_FALLBACK_FORMAT = "HH:mm";

const EM_DASH = "—";

// ---------------------------------------------------------------------------
// Module-level format refs
// ---------------------------------------------------------------------------

let orgDateFormatRef: string | null = null;
let orgTimeFormatRef: string | null = null;

/** The org's preferred date-fns date format, or null to fall back to the UK default. */
export function getOrgDateFormat(): string | null {
  return orgDateFormatRef;
}

/** The org's preferred date-fns time format, or null to fall back to the UK default. */
export function getOrgTimeFormat(): string | null {
  return orgTimeFormatRef;
}

/**
 * Stamps the module-level date/time format refs. Called by the surface sync hook
 * each time organisation settings resolve. Pass `null` for either argument to
 * clear that format and fall back to the deterministic UK default.
 */
export function setOrgFormats(dateFormat: string | null, timeFormat: string | null): void {
  orgDateFormatRef = dateFormat;
  orgTimeFormatRef = timeFormat;
}

// ---------------------------------------------------------------------------
// Effective format resolution
//
// The grammar gate (validateDateFormat/validateTimeFormat) is what catches the
// `tt` gotcha — `tt` is date-fns's seconds-timestamp token, not .NET AM/PM, so it
// renders garbage rather than throwing — before a bad stored format reaches
// date-fns. A non-grammar (legacy .NET) token therefore degrades to the UK default.
// ---------------------------------------------------------------------------

/** The org's effective date format: the configured override when grammar-valid, else the UK `dd/MM/yyyy` fallback. */
export function resolveOrgDateFormat(): string {
  const fmt = getOrgDateFormat();
  return fmt && validateDateFormat(fmt).valid ? fmt : ORG_DATE_FALLBACK_FORMAT;
}

/** The org's effective time format: the configured override when grammar-valid, else the UK `HH:mm` fallback. */
export function resolveOrgTimeFormat(): string {
  const fmt = getOrgTimeFormat();
  return fmt && validateTimeFormat(fmt).valid ? fmt : ORG_TIME_FALLBACK_FORMAT;
}

// ---------------------------------------------------------------------------
// Org-aware display formatters (delegate to formatDateSafe)
// ---------------------------------------------------------------------------

/**
 * Formats a date/ISO string for display in the org's dateFormat (UK `dd/MM/yyyy`
 * fallback) — never the OS locale. Returns em-dash for falsy/invalid.
 */
export function formatDate(dateStr?: string | null): string {
  return formatDateSafe(dateStr, resolveOrgDateFormat(), EM_DASH);
}

/**
 * Formats a date/ISO string with time in the org's date+time formats (UK
 * `dd/MM/yyyy HH:mm` fallback). Returns em-dash for falsy/invalid.
 */
export function formatDateTime(dateStr?: string | null): string {
  const datePart = formatDateSafe(dateStr, resolveOrgDateFormat(), EM_DASH);
  if (datePart === EM_DASH) return EM_DASH;
  return `${datePart} ${formatDateSafe(dateStr, resolveOrgTimeFormat(), EM_DASH)}`;
}

/**
 * Extracts the time portion from an ISO datetime string in the org's timeFormat
 * (UK `HH:mm` fallback). A bare `HH:mm`/`HH:mm:ss` value (no date component) is
 * anchored to a fixed date and rendered in the org's time format too — a 12-hour
 * org turns `14:30` into `2:30 PM`, it is NOT passed straight through. Returns
 * em-dash for falsy values, and the original string for anything that is neither
 * ISO nor a bare `HH:mm(:ss)`.
 */
export function formatTime(timeStr?: string | null): string {
  if (!timeStr) return EM_DASH;
  if (timeStr.includes("T")) {
    return formatDateSafe(timeStr, resolveOrgTimeFormat(), EM_DASH);
  }
  // A bare "HH:mm" / "HH:mm:ss" carries no date, so anchor it to a fixed date and
  // render it in the org's time format — a 12-hour org format must still turn
  // "14:30" into "2:30 PM", not pass the 24-hour value straight through. Match,
  // anchor and fall back on ONE trimmed value so a padded input (" 14:30 ") is
  // converted too rather than building an invalid anchored string that silently
  // bypasses the org conversion.
  const trimmed = timeStr.trim();
  const parts = /^(\d{2}):(\d{2})(:\d{2})?$/.exec(trimmed);
  if (!parts) return timeStr;
  const anchored = `2000-01-01T${parts[3] ? trimmed : `${trimmed}:00`}`;
  return formatDateSafe(anchored, resolveOrgTimeFormat(), trimmed);
}

/**
 * Formats a date/ISO string in the org's dateFormat (UK fallback), or returns
 * `"Present"` for falsy/invalid values. Use for open-ended ranges where a missing
 * end date means "still active" — distinct from {@link formatDate} which returns em-dash.
 */
export function formatDateOrPresent(dateStr?: string | null): string {
  if (!dateStr) return "Present";
  return formatDateSafe(dateStr, resolveOrgDateFormat(), "Present");
}

/**
 * Returns a short relative-time phrase — "Just now", "5m ago", "3h ago", "2d ago" —
 * or the absolute org-formatted datetime for anything older than a week. Returns
 * em-dash for falsy/unparseable values. `now` is injectable for tests.
 *
 * The org-aware sibling of core-utils `formatTimeAgo`, which shares the tier ladder
 * but falls back to the ISO date (not the org format) and returns "" for empty.
 */
export function formatRelativeTime(dateStr?: string | null, now: Date = new Date()): string {
  if (!dateStr) return EM_DASH;
  const then = new Date(dateStr).getTime();
  if (Number.isNaN(then)) return EM_DASH;
  const diffMinutes = Math.floor((now.getTime() - then) / MS_PER_MINUTE);
  // Shares the tier ladder with core-utils `formatTimeAgo`; the only difference is
  // the older-than-a-week fallback (org datetime here vs the plain ISO date there).
  return relativeTimePhrase(diffMinutes) ?? formatDateTime(dateStr);
}

// ---------------------------------------------------------------------------
// MUI X (dayjs) input-mask derivations
// ---------------------------------------------------------------------------

/** Converts one letter-run token from date-fns to dayjs (families that differ; others pass through). */
function convertTokenRun(run: string): string {
  switch (run[0]) {
    case "y":
      return "Y".repeat(run.length); // yyyy -> YYYY, yy -> YY
    case "d":
      return "D".repeat(run.length); // dd -> DD, d -> D
    case "a":
      return "A"; // date-fns AM/PM -> dayjs AM/PM
    case "E":
      // date-fns weekday widths -> dayjs: full (EEEE) -> dddd, short (E-EEE) -> ddd.
      return run.length >= 4 ? "dddd" : "ddd";
    default:
      return run; // M, H, h, m, s and any other letter run are identical
  }
}

/**
 * Converts a date-fns format string to the equivalent dayjs tokens. Only four token
 * families differ — year (`y`->`Y`), day-of-month (`d`->`D`), weekday (`E`->`d…`)
 * and meridiem (`a`->`A`); month, hour, minute and second are identical.
 *
 * Quoted literals are handled properly rather than blindly regex-replaced: date-fns
 * escapes literal text with single quotes (`dd 'day' MMM` renders a literal "day"),
 * which must NOT have its letters rewritten and must become a dayjs bracket literal
 * (`[day]`); `''` is a literal apostrophe.
 */
export function dateFnsToDayjsFormat(fmt: string): string {
  let out = "";
  let i = 0;
  while (i < fmt.length) {
    const ch = fmt[i];
    if (ch === "'") {
      // Consume a quoted literal span; '' inside it is an escaped apostrophe.
      i++;
      let literal = "";
      while (i < fmt.length) {
        if (fmt[i] === "'") {
          if (fmt[i + 1] === "'") {
            literal += "'";
            i += 2;
            continue;
          }
          i++;
          break;
        }
        literal += fmt[i];
        i++;
      }
      out += literal === "" ? "[']" : `[${literal}]`;
      continue;
    }
    if (/[a-zA-Z]/.test(ch)) {
      let run = ch;
      while (i + 1 < fmt.length && fmt[i + 1] === ch) {
        run += fmt[i + 1];
        i++;
      }
      out += convertTokenRun(run);
      i++;
      continue;
    }
    out += ch;
    i++;
  }
  return out;
}

/**
 * The org's date mask as dayjs tokens for a MUI X date picker — the effective
 * date format converted to dayjs tokens, so the UK fallback yields `DD/MM/YYYY`.
 * Mirrors {@link formatDate} so an editable input shows the read-only format.
 */
export function getOrgDateInputFormat(): string {
  return dateFnsToDayjsFormat(resolveOrgDateFormat());
}

/**
 * The org's date+time mask as dayjs tokens for a datetime picker, combining
 * {@link getOrgDateInputFormat} with the effective time format (UK fallback yields `HH:mm`).
 */
export function getOrgDateTimeInputFormat(): string {
  return `${getOrgDateInputFormat()} ${dateFnsToDayjsFormat(resolveOrgTimeFormat())}`;
}
